import { useQuery } from '@tanstack/react-query'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import {
  type SharePointSyncedFilesCountResponse,
  sharepointSyncedFilesCountQueryKeys,
} from '@/hooks/sharepoint/types'
import { useAuthInfoWithThrow } from '@/hooks/user/useAuthInfoWithThrow'

import type { UUID } from '@/types/Id'

interface UseSharepointSyncedFilesCountArgs {
  orgId: UUID | ''
  enabled?: boolean
}

export const fetchSharepointSyncedFilesCount = async (
  orgId: UUID | '',
  accessToken: string,
): Promise<SharePointSyncedFilesCountResponse> => {
  if (!orgId) throw new Error('No org ID provided.')
  const url = `${API_URL}/api/v1/organizations/${orgId}/sharepoint/synced-files/count`
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!res.ok)
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: res,
    })
  return (await res.json()) as SharePointSyncedFilesCountResponse
}

export const useSharepointSyncedFilesCount = ({
  orgId,
  enabled = true,
}: UseSharepointSyncedFilesCountArgs) => {
  const { accessToken } = useAuthInfoWithThrow()

  return useQuery({
    queryKey: sharepointSyncedFilesCountQueryKeys.organization({ orgId }),
    queryFn: () => fetchSharepointSyncedFilesCount(orgId, accessToken),
    enabled: !!orgId && enabled,
    staleTime: 30 * 1000, // 30 seconds
  })
}
